import { motion } from 'motion/react';
import type { FeatureRow, FeatureRating } from '../../data/competitors';

interface FeatureComparisonTableProps {
  features: FeatureRow[];
  competitorName: string;
}

function RatingIcon({ rating }: { rating: FeatureRating }) {
  if (rating === 'full') {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400" title="Full support">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
      </span>
    );
  }
  if (rating === 'partial') {
    return (
      <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400" title="Partial support">
        <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><circle cx="10" cy="10" r="5" /></svg>
      </span>
    );
  }
  return (
    <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-slate-100 dark:bg-neutral-800 text-slate-400 dark:text-neutral-600" title="Not available">
      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
    </span>
  );
}

export function FeatureComparisonTable({ features, competitorName }: FeatureComparisonTableProps) {
  // Group features by category
  const categories = [...new Set(features.map((f) => f.category))];

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">Feature Comparison</h2>
          <p className="text-lg text-slate-600 dark:text-neutral-400">
            See how Pollenate stacks up against {competitorName} feature by feature.
          </p>
        </motion.div>

        {/* Legend */}
        <div className="flex flex-wrap items-center justify-center gap-6 mb-8 text-sm text-slate-500 dark:text-neutral-400">
          <div className="flex items-center gap-2"><RatingIcon rating="full" /> Full support</div>
          <div className="flex items-center gap-2"><RatingIcon rating="partial" /> Partial</div>
          <div className="flex items-center gap-2"><RatingIcon rating="none" /> Not available</div>
        </div>

        {/* Table — desktop */}
        <div className="hidden sm:block">
          <div className="border border-slate-200 dark:border-neutral-800 rounded-xl overflow-hidden">
            {/* Header */}
            <div className="grid grid-cols-[1fr_120px_120px] bg-slate-50 dark:bg-neutral-900 border-b border-slate-200 dark:border-neutral-800 px-6 py-4">
              <span className="text-sm font-semibold text-slate-500 dark:text-neutral-400">Feature</span>
              <span className="text-sm font-semibold text-center text-amber-600 dark:text-amber-400">Pollenate</span>
              <span className="text-sm font-semibold text-center text-slate-500 dark:text-neutral-400">{competitorName}</span>
            </div>

            {categories.map((category) => (
              <div key={category}>
                {/* Category header */}
                <div className="px-6 py-3 bg-slate-50/50 dark:bg-neutral-900/50 border-b border-slate-200 dark:border-neutral-800">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-neutral-500">{category}</span>
                </div>
                {/* Feature rows */}
                {features.filter((f) => f.category === category).map((feature) => (
                  <div key={feature.name} className="grid grid-cols-[1fr_120px_120px] items-center px-6 py-3 border-b border-slate-100 dark:border-neutral-800/50 hover:bg-slate-50 dark:hover:bg-neutral-900/30 transition-colors">
                    <div>
                      <span className="text-sm text-slate-700 dark:text-neutral-300">{feature.name}</span>
                      {feature.note && <span className="block text-xs text-slate-400 dark:text-neutral-500 mt-0.5">{feature.note}</span>}
                    </div>
                    <div className="flex justify-center"><RatingIcon rating={feature.pollenate} /></div>
                    <div className="flex justify-center"><RatingIcon rating={feature.competitor} /></div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        {/* Cards — mobile */}
        <div className="sm:hidden space-y-6">
          {categories.map((category) => (
            <div key={category}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-neutral-500 mb-3">{category}</h3>
              <div className="space-y-2">
                {features.filter((f) => f.category === category).map((feature) => (
                  <div key={feature.name} className="bg-white dark:bg-neutral-900 border border-slate-200 dark:border-neutral-800 rounded-lg p-4">
                    <p className="text-sm font-medium text-slate-700 dark:text-neutral-300 mb-2">{feature.name}</p>
                    {feature.note && <p className="text-xs text-slate-400 dark:text-neutral-500 mb-3">{feature.note}</p>}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <RatingIcon rating={feature.pollenate} />
                        <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Pollenate</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500 dark:text-neutral-400 font-medium">{competitorName}</span>
                        <RatingIcon rating={feature.competitor} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
